import React, { useState } from 'react';
import { Alert, Button, Empty, Input, Segmented, Switch, Tooltip } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { PayMethodEventDetailProductType } from '../../../../lib/api/payMethodExhibit';
import { EditableEventDetail, EditablePayMethod } from '../../types';
import { getAvailableProductTypes, PRODUCT_TYPE_LABEL } from '../../constants/payMethodEventDetail';
import { SettingsSection } from '../../common/SettingsSection';
import { moveItem } from '../../utils/settingsChanges';
import { BenefitEditorStyle } from './styles';

type PayMethodBenefitEditorProps = {
  payMethod: EditablePayMethod;
  disabled: boolean;
  onChange: (patch: Partial<EditablePayMethod>) => void;
};

export const PayMethodBenefitEditor = (props: PayMethodBenefitEditorProps): React.JSX.Element => {
  const { payMethod, disabled, onChange } = props;
  const productTypeList = getAvailableProductTypes(payMethod.type);
  const [selectedType, setSelectedType] = useState<PayMethodEventDetailProductType>(productTypeList[0]);
  const productType = productTypeList.includes(selectedType) ? selectedType : productTypeList[0];
  const detailList = payMethod.eventDetails.filter((detail) => detail.productType === productType);
  const visibleList = detailList.filter((detail) => detail.isActive && detail.content.trim());

  const replaceDetails = (next: EditableEventDetail[]): void => {
    const grouped = productTypeList.flatMap((type) =>
      type === productType ? next : payMethod.eventDetails.filter((detail) => detail.productType === type),
    );
    onChange({
      eventDetails: [
        ...grouped,
        ...payMethod.eventDetails.filter((detail) => !productTypeList.includes(detail.productType)),
      ],
    });
  };
  const updateLine = (index: number, patch: Partial<EditableEventDetail>): void =>
    replaceDetails(detailList.map((detail, position) => (position === index ? { ...detail, ...patch } : detail)));

  return (
    <SettingsSection title={payMethod.name} description="배지와 하단 안내문을 함께 관리합니다.">
      <BenefitEditorStyle>
        <div className="benefit-fields">
          {!payMethod.isExhibit && (
            <Alert
              className="settings-inline-alert"
              type="info"
              showIcon
              message="숨김 상태입니다. 문구를 편집해 두고 결제수단을 노출할 때 사용할 수 있습니다."
            />
          )}
          <div className="benefit-label">
            <label htmlFor={'benefit-badge-' + payMethod.id}>혜택 배지</label>
          </div>
          <Input
            id={'benefit-badge-' + payMethod.id}
            value={payMethod.eventName ?? ''}
            maxLength={15}
            showCount
            disabled={disabled}
            placeholder="예: 결제 혜택 안내"
            onChange={(event) => onChange({ eventName: event.target.value || null })}
          />
          <p className="benefit-hint">결제수단 이름과 함께 표시합니다. 비우면 배지가 사라집니다.</p>
          <div className="benefit-label">
            <span>하단 안내문</span>
            <Button
              icon={<PlusOutlined />}
              disabled={disabled}
              onClick={() =>
                replaceDetails([
                  ...detailList,
                  { productType, content: '', isActive: true, rowKey: `new-${crypto.randomUUID()}` },
                ])
              }
            >
              문구 추가
            </Button>
          </div>
          {productTypeList.length > 1 && (
            <Segmented
              className="benefit-group"
              block
              value={productType}
              options={productTypeList.map((type) => ({ value: type, label: PRODUCT_TYPE_LABEL[type] }))}
              onChange={(value) => setSelectedType(value as PayMethodEventDetailProductType)}
            />
          )}
          {detailList.length === 0 && (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="등록된 안내문이 없습니다." />
          )}
          {detailList.map((detail, index) => (
            <div className="benefit-line" key={detail.rowKey}>
              <div className="benefit-line-head">
                <span className="benefit-line-status">
                  <Switch
                    checked={detail.isActive}
                    disabled={disabled}
                    aria-label={'안내문 ' + (index + 1) + ' 노출'}
                    onChange={(checked) => updateLine(index, { isActive: checked })}
                  />
                  문구 {index + 1} · {detail.isActive ? '노출' : '숨김'}
                </span>
                <div className="benefit-line-actions">
                  <Tooltip title="위로">
                    <Button
                      size="small"
                      type="text"
                      icon={<ArrowUpOutlined />}
                      aria-label={'안내문 ' + (index + 1) + ' 위로'}
                      disabled={disabled || index === 0}
                      onClick={() => replaceDetails(moveItem(detailList, index, index - 1))}
                    />
                  </Tooltip>
                  <Tooltip title="아래로">
                    <Button
                      size="small"
                      type="text"
                      icon={<ArrowDownOutlined />}
                      aria-label={'안내문 ' + (index + 1) + ' 아래로'}
                      disabled={disabled || index === detailList.length - 1}
                      onClick={() => replaceDetails(moveItem(detailList, index, index + 1))}
                    />
                  </Tooltip>
                  <Tooltip title="삭제">
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={'안내문 ' + (index + 1) + ' 삭제'}
                      disabled={disabled}
                      onClick={() => replaceDetails(detailList.filter((_, position) => position !== index))}
                    />
                  </Tooltip>
                </div>
              </div>
              <Input.TextArea
                aria-label={'안내문 ' + (index + 1)}
                value={detail.content}
                maxLength={255}
                showCount
                autoSize={{ minRows: 2, maxRows: 5 }}
                disabled={disabled}
                onChange={(event) => updateLine(index, { content: event.target.value })}
                placeholder="고객에게 보여줄 안내문을 입력하세요."
              />
            </div>
          ))}
        </div>
        <div className="benefit-preview">
          <div className="preview-label">
            <span>문구 미리보기</span>
            <span>{PRODUCT_TYPE_LABEL[productType]}</span>
          </div>
          {payMethod.isExhibit ? (
            <>
              <div className="preview-method">
                {payMethod.name}
                {payMethod.eventName?.trim() && <span className="preview-badge">{payMethod.eventName}</span>}
              </div>
              {visibleList.length > 0 && (
                <ul className="preview-details">
                  {visibleList.map((detail, index) => (
                    <li key={index}>{detail.content}</li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <span className="settings-muted">고객에게 표시되지 않습니다.</span>
          )}
        </div>
      </BenefitEditorStyle>
    </SettingsSection>
  );
};
